import { IIntegrationApp, IIntegrationAppConfig, IntegrationConfigType } from 'types';

/**
 * Helpers for rendering POS integration settings.
 *
 * Builds a `(POS type, config key) -> catalog metadata` join map from the apps
 * catalog and formats a raw config value for display per the value matrix below.
 * Data-source-agnostic: the same helpers back both live config and saved snapshots.
 */

/** Display string for an unset/empty setting value. */
export const UNSET_DISPLAY = '--';

export interface IFormattedSettingValue {
  display: string;
  /** Full value to show in an (i) tooltip — set only when the displayed value is truncated (CSV/list). */
  tooltip?: string;
}

export interface IResolvedSettingRow {
  key: string;
  name: string;
  description?: string;
  value: IFormattedSettingValue;
  /** false when the (type, key) could not be resolved against the catalog (raw-key fallback). */
  resolved: boolean;
  /** true when the resolved catalog entry is flagged hidden/deprecated. */
  hidden: boolean;
}

/** A single stored setting as persisted in a snapshot / read from live config: `{ key, value }`. */
export interface IIntegrationSettingEntry {
  key: string;
  value?: string | boolean | null;
}

/** Finds the catalog app for a POS `type`, matching case-insensitively. */
export const findIntegrationApp = (apps: IIntegrationApp[] | null | undefined, type: string): IIntegrationApp | undefined => {
  const target = (type ?? '').toLowerCase();
  return (apps ?? []).find((app) => (app.type ?? '').toLowerCase() === target);
};

const isUnset = (value?: string | boolean | null): boolean => value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

const isTruthy = (value?: string | boolean | null): boolean => value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');

const parseCsv = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

/** How many list items to show inline before collapsing the remainder into a `+N more` tooltip. */
const CSV_PREVIEW_COUNT = 3;

/**
 * Formats a raw setting value into a display string (+ optional tooltip), per the design matrix:
 * - boolean -> `Enabled` / `Disabled`
 * - option  -> matching `options[].label` (falls back to the raw value if no option matches)
 * - string  -> pass-through; a comma-separated list shows the first few items inline and collapses
 *              the rest into a `+N more` tooltip that enumerates the full list (so the tooltip adds
 *              information rather than repeating what is already visible)
 * - unset/empty -> `--`
 * - unresolved (type, key) -> raw value, no tooltip (never throws)
 */
export const formatIntegrationSettingValue = (config: IIntegrationAppConfig | undefined, value?: string | boolean | null): IFormattedSettingValue => {
  if (isUnset(value)) return { display: UNSET_DISPLAY };

  // Raw-key fallback: no catalog metadata — render the raw value, no tooltip.
  if (!config) return { display: String(value) };

  switch (config.type) {
    case IntegrationConfigType.BOOLEAN:
      return { display: isTruthy(value) ? 'Enabled' : 'Disabled' };

    case IntegrationConfigType.OPTION: {
      const match = (config.options ?? []).find((option) => option.value === value);
      return { display: match?.label ?? String(value) };
    }

    case IntegrationConfigType.STRING:
    default: {
      const stringValue = String(value);
      const items = parseCsv(stringValue);

      // Single value (or no list) — show it inline, no tooltip needed.
      if (items.length <= 1) return { display: stringValue };

      // Short list — fully visible inline, so a tooltip would just repeat it.
      if (items.length <= CSV_PREVIEW_COUNT) return { display: items.join(', ') };

      // Long list — preview the first few inline; the tooltip reveals the rest.
      const preview = items.slice(0, CSV_PREVIEW_COUNT).join(', ');
      return { display: `${preview} +${items.length - CSV_PREVIEW_COUNT} more`, tooltip: items.join(', ') };
    }
  }
};

/**
 * Resolves stored settings against a catalog `app` into display-ready rows (name,
 * one-line description, formatted value), in stored order. Unknown keys fall back to
 * the raw key as the name and the raw value (never throws). Hidden/deprecated rows are
 * skipped when `hideHiddenSettings` is set (they exist only to resolve historical keys).
 */
export const resolveIntegrationSettingRows = (app: IIntegrationApp | undefined, entries: IIntegrationSettingEntry[] | null | undefined, hideHiddenSettings = false): IResolvedSettingRow[] => {
  const configByKey = new Map((app?.config ?? []).map((config) => [config.key, config]));
  return (entries ?? []).reduce<IResolvedSettingRow[]>((rows, entry) => {
    const config = configByKey.get(entry.key);
    const hidden = Boolean(config?.hidden);
    if (hideHiddenSettings && hidden) return rows;
    rows.push({
      key: entry.key,
      name: config?.name ?? entry.key,
      description: config?.description,
      value: formatIntegrationSettingValue(config, entry.value),
      resolved: Boolean(config),
      hidden,
    });
    return rows;
  }, []);
};
